import { Trans } from '@lingui/react/macro';

import { DefectPhotos } from '@/components/DefectPhotos';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { getRefashionStatus } from '@/models/actionType';
import { TEMPORARY_ID_PREFIX } from '@/models/article';
import { type ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('actions-list');

export const ActionDefectPhotosMobile = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { errors } = useArticleContext();

  if (!(action ?? previousAction)?.media.length) {
    return null;
  }

  const actionErrors = action
    ? errors.services?.actions?.actions?.find((act) => act.id === action.id)
    : undefined;
  const hasActionError = Boolean(
    actionErrors?.missingPhotos || actionErrors?.missingRefashionPhoto
  );

  return (
    <div
      className={element('card-photos', {
        edited: getEditedFields({ action, previousAction }).isPhotosEdited,
      })}
    >
      <DefectPhotos
        action={action ?? previousAction}
        size="medium"
        isDisabled
        isInvalid={!!action && hasActionError}
      />
    </div>
  );
};

export const ActionDefectPhotosInput = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { errors, view } = useArticleContext();

  const isEditable =
    !!action &&
    view.services.actions.fields.photo.editable &&
    !action.id.startsWith(TEMPORARY_ID_PREFIX);

  const isRefashionAction = getRefashionStatus(action?.organizationPrice) === 'applied';

  const isPhotoOptionalForThisAction =
    isEditable && !view.services.actions.fields.photo.required && !isRefashionAction;

  const actionErrors = action
    ? errors.services?.actions?.actions?.find((act) => act.id === action.id)
    : undefined;
  const hasActionError = Boolean(
    actionErrors?.missingPhotos || actionErrors?.missingRefashionPhoto
  );

  return (
    <div
      className={element('card-photos', {
        edited: getEditedFields({ action, previousAction }).isPhotosEdited,
      })}
    >
      <DefectPhotos
        action={action ?? previousAction}
        size="medium"
        isDisabled={!isEditable}
        isInvalid={hasActionError}
        label={
          <>
            <Trans id="article.form.actions.photos.label">Photos</Trans>
            {isPhotoOptionalForThisAction && <OptionalLabel />}
          </>
        }
        secondaryActionIconOnly
      />
    </div>
  );
};
